import React, { useState, useEffect, useRef } from "react";
import { getSortedCategories, getMenuItemsForCategory } from "../data/menuAdapter";
import { CATEGORY_ILLUSTRATIONS } from "../data/assets";
import Reveal from "../components/Reveal";
import ProductDetailModal from "../components/ProductDetailModal";
import { useI18n } from "../data/i18n";
import { ArrowUp } from "lucide-react";
import Logo from "../components/Logo";

const DiamondDivider = () => (
  <div className="flex items-center justify-center gap-2 my-4">
    <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#e4c590]"></div>
    <span className="diamond-mark"></span>
    <span className="diamond-mark opacity-60"></span>
    <span className="diamond-mark"></span>
    <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#e4c590]"></div>
  </div>
);

const MenuItems = () => {
  const { t, lang, setLang } = useI18n();
  const [activeCat, setActiveCat] = useState("MAIN");
  const [selectedItem, setSelectedItem] = useState(null);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isSticky, setIsSticky] = useState(false);
  const stickySentinelRef = useRef(null);

  useEffect(() => {
    // Зареждане на категориите
    const sortedCats = getSortedCategories();
    setCategories(sortedCats);
    if (sortedCats.length > 0 && !sortedCats.find(c => c.key === activeCat)) {
      setActiveCat(sortedCats[0].key);
    }
  }, [activeCat]);

  useEffect(() => {
    // Зареждане на items при смяна на категория или език
    const menuItems = getMenuItemsForCategory(activeCat, lang);
    setItems(menuItems);
  }, [activeCat, lang]);


  useEffect(() => {
    const sentinel = stickySentinelRef.current;
    if (!sentinel) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      setIsSticky(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    }, { threshold: 0 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.body.classList.toggle("menu-categories-sticky", isSticky);
    return () => document.body.classList.remove("menu-categories-sticky");
  }, [isSticky]);

  const LangToggle = () => (
    <div className="lang-toggle flex-shrink-0">
      <button
        onClick={() => setLang("bg")}
        className={`lang-toggle-btn ${lang === "bg" ? "is-active" : ""}`}
        aria-label="Switch to Bulgarian"
      >BG</button>
      <span className="lang-toggle-sep">/</span>
      <button
        onClick={() => setLang("en")}
        className={`lang-toggle-btn ${lang === "en" ? "is-active" : ""}`}
        aria-label="Switch to English"
      >EN</button>
    </div>
  );

  const handleCategoryChange = (categoryKey) => {
    const sentinel = stickySentinelRef.current;
    const targetTop = sentinel ? window.scrollY + sentinel.getBoundingClientRect().top + 2 : 0;

    setActiveCat(categoryKey);
    window.scrollTo({ top: targetTop, behavior: "smooth" });
  };
  // Функция за получаване на преведено име на категория
  const getCategoryDisplayName = (categoryKey) => {
    // Първо пробваме с t(`cat.${categoryKey}`) ако има такъв ключ в i18n
    const translated = t(`cat.${categoryKey}`);
    if (translated !== `cat.${categoryKey}`) {
      return translated;
    }
    // Иначе връщаме от нашите преводи
    return t(`menu.category.${categoryKey}`);
  };

  return (
    <div className="bg-[#0a0a0a] pt-32 pb-24 min-h-screen relative overflow-x-clip">
      <div className="max-w-7xl mx-auto px-6 relative">
        <Reveal direction="up">
          <div className="text-center mb-10">
            <h1 className="display text-5xl md:text-7xl text-[#f5f0e6]">{t("menu.title")}</h1>
            <DiamondDivider />
          </div>
        </Reveal>

        <div ref={stickySentinelRef} className="h-px" aria-hidden="true" />
        <div className="sticky top-0 z-40 mb-12">
          <div className={`${isSticky ? "relative left-1/2 w-screen -translate-x-1/2 px-6 lg:px-12" : "-mx-6 px-6"} bg-[#0a0a0a]/95 backdrop-blur-md`}>
          <div className="grid grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 py-3 border-y border-[#1f1f1f]">
            {isSticky && <Logo size="sm" className="hidden lg:inline-flex flex-shrink-0" />}
            <div className="min-w-0 flex-1 overflow-x-auto">
              <div className="flex gap-2 md:gap-4 min-w-max md:justify-center">
              {categories.map((cat) => {
                const isActive = activeCat === cat.key;
                return (
                  <button
                    key={cat.key}
                    onClick={() => handleCategoryChange(cat.key)}
                    className={`flex flex-col items-center gap-1 px-5 py-3 transition-all duration-300 ${
                      isActive ? "bg-[#e4c590] text-[#0a0a0a]" : "text-[#a8a297] hover:text-[#f5f0e6]"
                    }`}
                  >
                    <img
                      src={CATEGORY_ILLUSTRATIONS[cat.key]}
                      alt={getCategoryDisplayName(cat.key)}
                      className="w-12 h-12 object-contain"
                      style={isActive ? { filter: "brightness(0)" } : {}}
                    />
                    <span className="text-xs uppercase tracking-widest font-medium">
                      {getCategoryDisplayName(cat.key)}
                    </span>
                  </button>
                );
              })}
              </div>
            </div>
            {isSticky && <LangToggle />}
          </div>
        </div>
        </div>

        <Reveal direction="fade" key={activeCat}>
          <div className="text-center mb-12">
            <p className="section-label">{t("menu.category")}</p>
            <h2 className="display text-4xl md:text-6xl text-[#e4c590] uppercase tracking-wider mt-2">
              {getCategoryDisplayName(activeCat)}
            </h2>
            <div className="w-32 h-px bg-[#1f1f1f] mx-auto mt-6"></div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((item, idx) => (
            <Reveal key={`${activeCat}-${idx}`} direction="up" delay={(idx % 6) * 100}>
              <button
                onClick={() => setSelectedItem(item)}
                className="text-left bg-[#0f0f0f] border border-[#1f1f1f] overflow-hidden group hover:border-[#e4c590]/60 transition-colors duration-300 w-full h-full flex flex-col cursor-pointer"
              >
                {item.image && (
                  <div className="aspect-[4/3] overflow-hidden bg-[#0a0a0a]">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                    />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="display text-2xl text-[#f5f0e6] mb-2 group-hover:text-[#e4c590] transition-colors">
                    {item.name}
                  </h3>
                  {item.desc && (
                    <p className="text-[#a8a297] text-sm leading-relaxed mb-4 line-clamp-2 flex-1">
                      {item.desc}
                    </p>
                  )}
                  <div className="flex items-center justify-between pt-4 border-t border-[#1f1f1f]">
                    <span className="text-[#7a7468] text-xs uppercase tracking-widest">{item.weight}</span>
                    <span className="text-[#e4c590] text-sm font-medium whitespace-nowrap">{item.priceRaw}</span>
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        {items.length === 0 && <p className="text-center text-[#a8a297]">{t("menu.no_items")}</p>}
      </div>

      {isSticky && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed right-5 bottom-5 md:right-8 md:bottom-8 z-50 w-12 h-12 rounded-full border border-[#e4c590]/60 bg-[#0a0a0a]/95 text-[#e4c590] flex items-center justify-center shadow-lg hover:bg-[#e4c590] hover:text-[#0a0a0a] transition-colors"
          aria-label="Back to top"
        >
          <ArrowUp size={20} />
        </button>
      )}

      <ProductDetailModal
        item={selectedItem}
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
      />
    </div>
  );
};

export default MenuItems;